import Close from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import useGetArrangedRows from 'api/inventoryGeo/useGetArrangedRows';
import useEntryWorkspaceMutations from 'api/operation/entry/useEntryWorkspaceMutations';
import ShelfAndPartMap from 'components/shared/WarehouseGeoGraphMap/ShelfAndPartMap/ShelfAndPartMap';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

const STEPS = {
  SECTION: 'section',
  AREA: 'area',
  SHELF: 'shelf',
  BASKET: 'basket',
};

const stepTitles = {
  [STEPS.SECTION]: 'لطفا بخش مورد نظر را انتخاب کنید',
  [STEPS.AREA]: 'لطفا ناحیه مورد نظر را انتخاب کنید',
  [STEPS.SHELF]: 'قفسه و بخش را انتخاب کنید',
  [STEPS.BASKET]: 'تعداد و سبد را انتخاب کنید',
};

const toShelfAndPartMapData = shelves => {
  if (!Array.isArray(shelves)) return [];

  return shelves.map(shelf => ({
    guid: shelf.guid,
    display: shelf.display,
    parts: (shelf.parts || [])
      .filter(part => part.isVisible !== false)
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(part => ({
        guid: part.guid,
        display: part.display,
        isFull: Boolean(part.isFull),
        status: part.status,
        cellCount: Number(part.cellCount) || 0,
        emptyCellCount: Number(part.emptyCellCount) || 0,
        productCellCount: Number(part.productCellCount) || 0,
        cells: part.cells || [],
      })),
  }));
};

// eslint-disable-next-line complexity
const PlacementFlow = ({
  open,
  onClose,
  operationGuid,
  product,
  onCompleted,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = operationSettings?.defaultZone?.key;
  const { enqueueSnackbar } = useSnackbar();
  const mutations = useEntryWorkspaceMutations();
  const {
    getArrangedRows,
    arrangedRows,
    loading: arrangedRowsLoading,
  } = useGetArrangedRows();

  const [step, setStep] = useState(STEPS.SECTION);
  const [loading, setLoading] = useState(false);
  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [areas, setAreas] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [baskets, setBaskets] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [count, setCount] = useState('1');

  const productPackGuid = product?.productPackGuid;
  const operationProductGuid = product?.operationProductGuid;

  const reset = () => {
    setStep(STEPS.SECTION);
    setSections([]);
    setAreas([]);
    setShelves([]);
    setBaskets([]);
    setSelectedSection(null);
    setSelectedArea(null);
    setSelectedPart(null);
    setSelectedCell(null);
    setCount('1');
    setIsViewerModalOpen(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (!zoneGuid || !productPackGuid) {
      enqueueSnackbar('منطقه یا بسته محصول معتبر نیست', { variant: 'error' });
      return;
    }
    setLoading(true);
    mutations
      .getPlacementSections(zoneGuid, productPackGuid)
      .then(res => {
        setSections(Array.isArray(res) ? res : res?.list ?? []);
        setStep(STEPS.SECTION);
      })
      .finally(() => setLoading(false));
  }, [open, zoneGuid, productPackGuid]);

  const selectSection = async section => {
    setSelectedSection(section);
    setLoading(true);
    const res = await mutations.getPlacementAreas(
      section.guid,
      productPackGuid,
    );
    setLoading(false);
    setAreas(Array.isArray(res) ? res : res?.list ?? []);
    setStep(STEPS.AREA);
  };

  const selectArea = async area => {
    setSelectedArea(area);
    setLoading(true);
    const res = await mutations.getShelfAndPart(area.guid, productPackGuid);
    setLoading(false);
    setShelves(Array.isArray(res) ? res : res?.list ?? []);
    setStep(STEPS.SHELF);
  };

  const selectPart = part => {
    setSelectedPart(part);
    setIsViewerModalOpen(true);
    getArrangedRows({ guid: part.guid, productPackGuid });
  };

  const handleClickPart = partGuid => {
    const part = shelves
      .flatMap(shelf => shelf.parts || [])
      .find(p => p.guid === partGuid);
    if (part) {
      selectPart(part);
    }
  };

  const closeViewerModal = () => {
    setIsViewerModalOpen(false);
  };

  const shelfMapData = toShelfAndPartMapData(shelves);

  const selectCell = async cellItem => {
    const cell = cellItem.cell || cellItem;
    if (cell.readOnly) {
      enqueueSnackbar('امکان ثبت محصول در این مکان وجود ندارد', {
        variant: 'warning',
      });
      return;
    }
    setSelectedCell(cell);
    setIsViewerModalOpen(false);
    setLoading(true);
    const res = await mutations.getAvailableShoppingCarts(
      operationGuid,
      selectedSection?.guid,
    );
    setLoading(false);
    setBaskets(Array.isArray(res) ? res : res?.list ?? []);
    setStep(STEPS.BASKET);
  };

  const reopenCellViewer = () => {
    setStep(STEPS.SHELF);
    setIsViewerModalOpen(true);
  };

  const submitInit = async basket => {
    const parsedCount = Number.parseInt(count, 10);
    if (!Number.isFinite(parsedCount) || parsedCount <= 0) {
      enqueueSnackbar('تعداد معتبر نیست', { variant: 'warning' });
      return;
    }
    setLoading(true);
    const ok = await mutations.initTask({
      operationGuid,
      operationProductGuid,
      addressGuid: selectedCell.guid,
      shoppingCartGuid: basket.key || basket.guid,
      count: String(parsedCount),
    });
    setLoading(false);
    if (ok) {
      onCompleted?.();
      onClose?.();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={step === STEPS.SHELF ? 'full' : 'sm'}
      sx={{
        direction: 'rtl',
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {stepTitles[step]}
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {product ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {product.title} - {product.packName} - x{product.quantity}
          </Typography>
        ) : null}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : null}

        {!loading && step === STEPS.SECTION ? (
          <Stack gap={1}>
            {sections.map(section => (
              <Button
                key={section.guid}
                variant="contained"
                onClick={() => selectSection(section)}
              >
                {section.display}
                <Typography variant="body2" sx={{ mr: 1 }}>
                  ({section.cellCount ?? 0})
                </Typography>
              </Button>
            ))}
          </Stack>
        ) : null}

        {!loading && step === STEPS.AREA ? (
          <Stack gap={1}>
            {areas.map(area => (
              <Button
                key={area.guid}
                variant="contained"
                sx={{ direction: 'rtl' }}
                onClick={() => selectArea(area)}
              >
                {area.display}
                <Typography variant="body2" sx={{ mr: 1 }}>
                  ({area.cellCount ?? 0})
                </Typography>
              </Button>
            ))}
            <Button onClick={() => setStep(STEPS.SECTION)}>بازگشت</Button>
          </Stack>
        ) : null}

        {!loading && step === STEPS.SHELF ? (
          <Stack gap={2}>
            <Box sx={{ width: '100%', direction: 'rtl', overflow: 'auto' }}>
              <ShelfAndPartMap
                loading={false}
                areaGuid={selectedArea?.guid}
                shelfsData={shelfMapData}
                isViewerModalOpen={isViewerModalOpen}
                onClickPart={handleClickPart}
                closeViewerModal={closeViewerModal}
                partDisplay={selectedPart?.display || selectedPart?.partDisplay}
                arrangedRows={arrangedRows}
                arrangedRowsLoading={arrangedRowsLoading}
                onSelectCell={selectCell}
              />
            </Box>
            <Button onClick={() => setStep(STEPS.AREA)}>بازگشت</Button>
          </Stack>
        ) : null}

        {!loading && step === STEPS.BASKET ? (
          <Stack gap={1.5}>
            <TextField
              label="تعداد"
              type="number"
              value={count}
              onChange={e => setCount(e.target.value)}
            />
            {baskets.map(basket => (
              <LoadingButton
                key={basket.key || basket.guid}
                variant="contained"
                loading={loading}
                onClick={() => submitInit(basket)}
              >
                {basket.value || basket.title}
              </LoadingButton>
            ))}
            {!baskets.length ? (
              <Typography color="text.secondary">
                سبدی برای این بخش یافت نشد
              </Typography>
            ) : null}
            <Button onClick={reopenCellViewer}>بازگشت</Button>
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default PlacementFlow;
